import axios from 'axios';

/**
 * ============================================================================
 * ADVANCED CRITERIA API - Conditional Chaining Support
 * ============================================================================
 * API client functions for the advanced lender criteria system with
 * conditional IF-THEN-ELSE logic, AND/OR operators, and nested rules.
 */

// ============================================================================
// SETTINGS ROUTES
// ============================================================================

/**
 * Get advanced criteria settings for all lenders
 * 
 * @returns {Promise<Object>} Advanced criteria settings
 */
export const getAdvancedCriteriaSettings = async () => {
  try {
    const response = await axios.get('/api/advanced-criteria/settings');
    return response.data;
  } catch (error) {
    console.error('Error fetching advanced criteria settings:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Toggle advanced criteria enabled status
 * 
 * @param {boolean} enabled - Whether advanced criteria should be enabled
 * @returns {Promise<Object>} Response with updated enabled status
 */
export const toggleAdvancedCriteria = async (enabled) => {
  try {
    const response = await axios.put('/api/advanced-criteria/enabled', { enabled });
    return response.data;
  } catch (error) {
    console.error('Error toggling advanced criteria:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Get all available fields for criteria configuration
 * 
 * @returns {Promise<Array>} Array of available fields
 */
export const getAdvancedCriteriaFields = async () => {
  try {
    const response = await axios.get('/api/advanced-criteria/available-fields');
    return response.data;
  } catch (error) {
    console.error('Error fetching available fields:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Sync lenders from active lenders to advanced criteria
 * 
 * @returns {Promise<Object>} Response with sync status
 */
export const syncLendersToAdvancedCriteria = async () => {
  try {
    const response = await axios.post('/api/advanced-criteria/sync-lenders');
    return response.data;
  } catch (error) {
    console.error('Error syncing lenders:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Fix lender IDs to match UI format (stringId)
 * This is a one-time migration to fix existing data
 * 
 * @returns {Promise<Object>} Response with fix status
 */
export const fixLenderIds = async () => {
  try {
    const response = await axios.post('/api/advanced-criteria/fix-lender-ids');
    return response.data;
  } catch (error) {
    console.error('Error fixing lender IDs:', error);
    throw error.response ? error.response.data : error;
  }
};

// ============================================================================
// SIMPLE CRITERIA ROUTES
// ============================================================================

/**
 * Update simple criteria for a lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {Array} simpleCriteria - Array of simple criterion objects
 * @returns {Promise<Object>} Response with update status
 */
export const updateSimpleCriteria = async (lenderId, simpleCriteria) => {
  try {
    const response = await axios.post(`/api/advanced-criteria/lender/${lenderId}/simple-criteria`, {
      simpleCriteria
    });
    return response.data;
  } catch (error) {
    console.error('Error updating simple criteria:', error);
    throw error.response ? error.response.data : error;
  }
};

// ============================================================================
// CONDITIONAL RULES ROUTES
// ============================================================================

/**
 * Add a new conditional rule to a lender
 * 
 * @param {string} lenderId - ID of the lender
 * @param {Object} rule - The conditional rule object
 * @returns {Promise<Object>} Response with the created rule
 */
export const addConditionalRule = async (lenderId, rule) => {
  try {
    const response = await axios.post(`/api/advanced-criteria/lender/${lenderId}/conditional-rule`, {
      rule
    });
    return response.data;
  } catch (error) {
    console.error('Error adding conditional rule:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Update a conditional rule
 * 
 * @param {string} lenderId - ID of the lender
 * @param {string} ruleId - ID of the rule to update
 * @param {Object} rule - The updated conditional rule object
 * @returns {Promise<Object>} Response with the updated rule
 */
export const updateConditionalRule = async (lenderId, ruleId, rule) => {
  try {
    const response = await axios.put(`/api/advanced-criteria/lender/${lenderId}/conditional-rule/${ruleId}`, {
      rule
    });
    return response.data;
  } catch (error) {
    console.error('Error updating conditional rule:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Delete a conditional rule
 * 
 * @param {string} lenderId - ID of the lender
 * @param {string} ruleId - ID of the rule to delete
 * @returns {Promise<Object>} Response with deletion status
 */
export const deleteConditionalRule = async (lenderId, ruleId) => {
  try {
    const response = await axios.delete(`/api/advanced-criteria/lender/${lenderId}/conditional-rule/${ruleId}`);
    return response.data;
  } catch (error) {
    console.error('Error deleting conditional rule:', error);
    throw error.response ? error.response.data : error;
  }
};

// ============================================================================
// BULK UPDATE ROUTES
// ============================================================================

/**
 * Update all criteria for a lender (simple + conditional)
 * 
 * @param {string} lenderId - ID of the lender
 * @param {Object} criteria - Object with simpleCriteria and conditionalRules arrays
 * @returns {Promise<Object>} Response with update status
 */
export const updateAllLenderCriteria = async (lenderId, { simpleCriteria, conditionalRules, hideUntilQualified }) => {
  try {
    const response = await axios.put(`/api/advanced-criteria/lender/${lenderId}`, {
      simpleCriteria,
      conditionalRules,
      hideUntilQualified
    });
    return response.data;
  } catch (error) {
    console.error('Error updating lender criteria:', error);
    throw error.response ? error.response.data : error;
  }
};

/**
 * Copy criteria from one lender to another
 * 
 * @param {string} sourceLenderId - ID of the source lender
 * @param {string} targetLenderId - ID of the target lender
 * @param {boolean} copySimple - Whether to copy simple criteria
 * @param {boolean} copyConditional - Whether to copy conditional rules
 * @returns {Promise<Object>} Response with copy status
 */
export const copyCriteria = async (sourceLenderId, targetLenderId, copySimple = true, copyConditional = true) => {
  try {
    const response = await axios.post('/api/advanced-criteria/copy-criteria', {
      sourceLenderId,
      targetLenderId,
      copySimple,
      copyConditional
    });
    return response.data;
  } catch (error) {
    console.error('Error copying criteria:', error);
    throw error.response ? error.response.data : error;
  }
};

// ============================================================================
// HELPER FUNCTIONS FOR BUILDING CRITERIA
// ============================================================================

/**
 * Create an empty condition group
 * 
 * @param {string} logic - 'AND' or 'OR'
 * @returns {Object} Empty condition group
 */
export const createConditionGroup = (logic = 'AND') => ({
  logic,
  conditions: [],
  groups: []
});

/**
 * Create a new condition
 * 
 * @param {string} fieldId - Field ID
 * @param {string} fieldName - Field display name
 * @param {string} operator - Comparison operator
 * @param {any} value - Comparison value
 * @returns {Object} Condition object
 */
export const createCondition = (fieldId, fieldName, operator, value) => ({
  fieldId,
  fieldName,
  operator,
  value
});

/**
 * Create a new criterion (for THEN/ELSE branches)
 * 
 * @param {string} fieldId - Field ID
 * @param {string} fieldName - Field display name
 * @param {string} operator - Comparison operator
 * @param {any} value - Comparison value
 * @param {number} weight - Weight (1-10)
 * @param {boolean} required - Whether this criterion is required
 * @returns {Object} Criterion object
 */
export const createCriterion = (fieldId, fieldName, operator, value, weight = 5, required = false) => ({
  fieldId,
  fieldName,
  operator,
  value,
  weight,
  required
});

/**
 * Create a new conditional rule
 * 
 * @param {string} name - Rule name
 * @param {Object} conditionGroup - The IF condition group
 * @param {Array} thenCriteria - THEN branch criteria
 * @param {Array} elseCriteria - ELSE branch criteria (optional)
 * @param {number} weight - Rule weight (1-10)
 * @param {boolean} required - Whether this rule is required
 * @returns {Object} Conditional rule object
 */
export const createConditionalRule = (
  name,
  conditionGroup = createConditionGroup(),
  thenCriteria = [],
  elseCriteria = [],
  weight = 5,
  required = false
) => ({
  name,
  enabled: true,
  weight,
  required,
  conditionGroup,
  thenCriteria,
  elseCriteria,
  nestedRules: []
});

// ============================================================================
// EVALUATION HELPER FUNCTIONS (for frontend use)
// ============================================================================

/**
 * Evaluate a single condition against a field value
 * 
 * @param {Object} condition - The condition to evaluate
 * @param {any} fieldValue - The actual field value
 * @returns {boolean} Whether the condition is met
 */
export const evaluateSingleCondition = (condition, fieldValue) => {
  const { operator, value } = condition;
  
  if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
    return false;
  }
  
  switch (operator) {
    case '=':
      return String(fieldValue).toLowerCase() === String(value).toLowerCase();
    case 'is_one_of':
      const options = Array.isArray(value) ? value : [value];
      return options.some(opt => String(fieldValue).toLowerCase() === String(opt).toLowerCase());
    case 'is_not':
      const excluded = Array.isArray(value) ? value : [value];
      return !excluded.some(opt => String(fieldValue).toLowerCase() === String(opt).toLowerCase());
    case '>':
      return parseFloat(fieldValue) > parseFloat(value);
    case '>=':
      return parseFloat(fieldValue) >= parseFloat(value);
    case '<':
      return parseFloat(fieldValue) < parseFloat(value);
    case '<=':
      return parseFloat(fieldValue) <= parseFloat(value);
    case 'contains':
      return String(fieldValue).toLowerCase().includes(String(value).toLowerCase());
    case 'older_than_months': {
      // Special handling for date fields
      if (!fieldValue) return false;
      const dateValue = new Date(fieldValue);
      if (isNaN(dateValue.getTime())) return false;
      const now = new Date();
      const monthsDiff = (now.getFullYear() - dateValue.getFullYear()) * 12 + 
                        (now.getMonth() - dateValue.getMonth());
      return monthsDiff >= parseInt(value, 10);
    }
    default:
      return false;
  }
};

/**
 * Evaluate a condition group recursively (AND/OR with nesting)
 * 
 * @param {Object} conditionGroup - The condition group to evaluate
 * @param {Function} getFieldValue - Function to get field values: (fieldId) => value
 * @returns {boolean} Whether the condition group is satisfied
 */
export const evaluateConditionGroup = (conditionGroup, getFieldValue) => {
  if (!conditionGroup) return true;
  
  const { logic, conditions = [], groups = [] } = conditionGroup;
  
  // Evaluate all leaf conditions
  const conditionResults = conditions.map(condition => {
    const fieldValue = getFieldValue(condition.fieldId);
    return evaluateSingleCondition(condition, fieldValue);
  });
  
  // Evaluate all nested groups recursively
  const groupResults = groups.map(group => 
    evaluateConditionGroup(group, getFieldValue)
  );
  
  // Combine all results
  const allResults = [...conditionResults, ...groupResults];
  
  if (allResults.length === 0) return true;
  
  // Apply AND/OR logic
  if (logic === 'AND') {
    return allResults.every(result => result === true);
  } else {  // OR
    return allResults.some(result => result === true);
  }
};

/**
 * Format a condition group as a human-readable string
 * 
 * @param {Object} conditionGroup - The condition group
 * @returns {string} Human-readable description
 */
export const formatConditionDescription = (conditionGroup) => {
  if (!conditionGroup) return '';
  
  const { logic, conditions = [], groups = [] } = conditionGroup;
  
  const operatorLabels = {
    '=': 'is',
    'is_one_of': 'is one of',
    'is_not': 'is not',
    '>': '>',
    '>=': '≥',
    '<': '<',
    '<=': '≤',
    'contains': 'contains',
    'older_than_months': 'is older than (months)'
  };
  
  const conditionTexts = conditions.map(c => {
    const opText = operatorLabels[c.operator] || c.operator;
    const valueText = Array.isArray(c.value) ? `[${c.value.join(', ')}]` : c.value;
    return `${c.fieldName || c.fieldId} ${opText} ${valueText}`;
  });
  
  const groupTexts = groups.map(g => `(${formatConditionDescription(g)})`);
  
  const allTexts = [...conditionTexts, ...groupTexts];
  const connector = logic === 'AND' ? ' AND ' : ' OR ';
  
  return allTexts.join(connector);
};

